// @react
import React from 'react'
// @types
import type { ReactElement } from 'react'
import type {
  GetUserQuery,
  GetUserQueryVariables,
} from 'lib/graphql/graphqlTypes'
// @graphql
import { useQuery } from '@apollo/client'
import QueryError from 'modules/common/components/Errors/QueryError'
import { GET_USER } from 'graphql/queries/user.query'
// @libraries
import { makeStyles } from '@material-ui/core/styles'
// @components
import Box from '@bit/atd.web.box'
import Grid from '@bit/atd.web.grid'
import Spacer from '@bit/atd.web.spacer'
import Button from '@bit/atd.web.button'
// @module components
import MembershipItem from 'modules/benefitsCenter/components/MembershipItem'
// @utils
import Helper from 'utils/Helper.class'

const useStyles = makeStyles(() => ({
  body: {
    paddingTop: '6px !important',
  },
  footer: {
    fontSize: '16px',
  },
  btn: {
    marginLeft: '-18px',
  },
}))

const MembershipDetails = (): ReactElement => {
  /******************************************
   * HOOKS
   ******************************************/

  const classes = useStyles()

  const { loading: isLoading, error: isError, data } = useQuery<
    GetUserQuery,
    GetUserQueryVariables
  >(GET_USER, { fetchPolicy: 'cache-first' })

  const { currentMembership, chapterMembership } = data?.user?.membership || {}

  const visibleChapters = chapterMembership?.slice(0, 2) || []

  /******************************************
   * RENDERS
   ******************************************/

  if (isLoading) {
    return <span>Loading...</span>
  }

  if (isError || !data) {
    return <QueryError />
  }

  return (
    <Box
      variant={'subModule'}
      header={{
        title: 'Membership Type',
      }}
      classNames={{
        body: classes.body,
        footer: classes.footer,
      }}
      footer={{
        link: {
          url: Helper.getPathFromRouteKey('PROFILE_MEMBERSHIPDETAILS'),
          label: 'Membership Details',
        },
      }}
    >
      {/* Membership */}
      <MembershipItem
        key="currentMembership"
        title={currentMembership?.memberTypeLabel || ''}
        startDate={currentMembership?.cycleBeginDate}
        endDate={currentMembership?.cycleEndDate}
      />

      {/* Chapters */}
      {visibleChapters.map(
        (
          { id, memberTypeLabel, cycleBeginDate, cycleEndDate, chapterURL },
          index,
        ) => (
          <div key={id}>
            {index === 0 && <Spacer fullWidth size={16} />}

            <MembershipItem
              title={memberTypeLabel || ''}
              startDate={cycleBeginDate}
              endDate={cycleEndDate}
              url={chapterURL}
              label="Visit Chapter Website"
            />

            <Spacer fullWidth size={16} />
          </div>
        ),
      )}

      {/* Show More */}
      {chapterMembership && chapterMembership.length > 2 && (
        <Grid item xs={12}>
          <Button
            href={Helper.getPathFromRouteKey('PROFILE_MEMBERSHIPDETAILS')}
            className={classes.btn}
            variant="tertiary"
          >
            {`+${
              (chapterMembership?.length || 0) - visibleChapters.length
            } more`}
          </Button>
        </Grid>
      )}
    </Box>
  )
}

export default MembershipDetails
